import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { ChevronDown, Heart } from "lucide-react";
import Petals from "../common/Petals";
import { useSmoothScroll } from "../../providers/SmoothScroll";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;

// ── Styles ───────────────────────────────────────────────────

const Wrapper = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100vh",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
});

// Calque image animé (zoom lent "Ken Burns")
const Bg = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage: "url('assets/home2.jpeg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  animation: "kenburns 22s ease-in-out infinite alternate",
});

// Bouton CTA doré, brillant, qui attire l'œil (pulsation douce)
const RsvpCta = styled(motion.button)({
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  border: "none",
  padding: "15px 42px",
  borderRadius: 40,
  fontFamily: fonts.sans,
  fontSize: "0.82rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "#fff",
  background: "linear-gradient(135deg, #CDAC70 0%, #A8884F 100%)",
  boxShadow: "0 14px 34px rgba(168,136,79,0.5)",
  animation: "ctaPulse 2.6s ease-in-out infinite",
  "@media (max-width:600px)": {
    padding: "14px 32px",
    fontSize: "0.76rem",
    letterSpacing: "0.14em",
  },
});

// Voile chaud et doux
const Scrim = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(to bottom, rgba(33,28,24,0.5) 0%, rgba(33,28,24,0.18) 38%, rgba(33,28,24,0.32) 70%, rgba(33,28,24,0.62) 100%)",
});

// ── Sous-composants ──────────────────────────────────────────

// Une phrase d'intro qui apparaît puis se fond
function Phrase({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -16, filter: "blur(8px)" }}
      transition={{ duration: 1, ease: EASE }}
      style={{
        fontFamily: fonts.serif,
        fontStyle: "italic",
        fontSize: "clamp(1.4rem, 3.6vw, 2.5rem)",
        letterSpacing: "0.03em",
        color: "#fdfbf7",
        textShadow: "0 2px 24px rgba(0,0,0,0.45)",
      }}
    >
      {text}
    </motion.div>
  );
}

// Mot révélé lettre par lettre
const lettersContainer = (delay = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: delay } },
});
const letterV: Variants = {
  hidden: { opacity: 0, y: "0.5em", filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: EASE },
  },
};

function Word({ text, delay = 0 }: { text: string; delay?: number }) {
  return (
    <motion.span
      variants={lettersContainer(delay)}
      initial="hidden"
      animate="visible"
      style={{ display: "inline-block", whiteSpace: "nowrap" }}
    >
      {Array.from(text).map((ch, i) => (
        <motion.span
          key={i}
          variants={letterV}
          style={{ display: "inline-block", whiteSpace: "pre" }}
        >
          {ch}
        </motion.span>
      ))}
    </motion.span>
  );
}

// ── Composant ────────────────────────────────────────────────

export default function Home() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const scrollTo = useSmoothScroll();
  const [stage, setStage] = useState(0); // 0: phrase 1 — 1: phrase 2 — 2: final

  useEffect(() => {
    if (reduce) {
      setStage(2);
      return;
    }
    const t1 = setTimeout(() => setStage(1), 2800);
    const t2 = setTimeout(() => setStage(2), 5400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reduce]);

  return (
    <Wrapper>
      <Bg />
      <Scrim />
      <Petals />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          color: "#fff",
          px: 2,
          width: "100%",
        }}
      >
        <AnimatePresence mode="wait">
          {stage === 0 && <Phrase key="c1" text={t("home.cine1")} />}
          {stage === 1 && <Phrase key="c2" text={t("home.cine2")} />}

          {stage === 2 && (
            <motion.div
              key="final"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              {/* Kicker */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.9, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                style={{
                  fontFamily: fonts.sans,
                  fontSize: "0.72rem",
                  letterSpacing: "0.42em",
                  textTransform: "uppercase",
                  marginBottom: "1rem",
                  color: palette.champagne,
                }}
              >
                {t("home.invited")}
              </motion.div>

              {/* Prénoms en script, lettre par lettre */}
              <Box
                sx={{
                  fontFamily: fonts.script,
                  fontSize: "clamp(3rem, 9vw, 6rem)",
                  lineHeight: 1.02,
                  textShadow: "0 4px 30px rgba(0,0,0,0.4)",
                }}
              >
                <Box>
                  <Word text={t("home.name1")} delay={0.2} />
                </Box>
                <Box
                  sx={{
                    color: palette.gold,
                    fontSize: "0.62em",
                    my: "-0.08em",
                  }}
                >
                  <Word text="&" delay={0.55} />
                </Box>
                <Box>
                  <Word text={t("home.name2")} delay={0.75} />
                </Box>
              </Box>

              {/* Divider animé */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  my: "20px",
                }}
              >
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1, delay: 1.1, ease: EASE }}
                  style={{
                    height: 1,
                    width: 90,
                    background:
                      "linear-gradient(90deg, transparent, rgba(231,216,196,0.9))",
                    transformOrigin: "right",
                  }}
                />
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.5, ease: EASE }}
                  style={{ color: palette.gold, fontSize: "0.9rem" }}
                >
                  ❦
                </motion.span>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1, delay: 1.1, ease: EASE }}
                  style={{
                    height: 1,
                    width: 90,
                    background:
                      "linear-gradient(90deg, rgba(231,216,196,0.9), transparent)",
                    transformOrigin: "left",
                  }}
                />
              </Box>

              {/* Date */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.95, y: 0 }}
                transition={{ duration: 0.8, delay: 1.6, ease: EASE }}
                style={{
                  fontFamily: fonts.sans,
                  fontSize: "0.8rem",
                  letterSpacing: "0.3em",
                  color: "#f3ece1",
                }}
              >
                {t("home.date")}
              </motion.div>

              {/* CTA — bouton bien visible vers le formulaire RSVP */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 2, ease: EASE }}
                style={{ marginTop: "34px" }}
              >
                <RsvpCta
                  type="button"
                  onClick={() => scrollTo("#rsvp")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 320, damping: 18 }}
                >
                  <Heart size={17} fill="#fff" strokeWidth={0} />
                  {t("home.rsvpCta")}
                </RsvpCta>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Indicateur de défilement */}
      {stage === 2 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ duration: 1, delay: 2 }}
          onClick={() => scrollTo("#story")}
          aria-label="Défiler vers le bas"
          style={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.9)",
            animation: "bounceDown 1.8s ease-in-out infinite",
          }}
        >
          <ChevronDown size={30} strokeWidth={1.5} />
        </motion.button>
      )}
    </Wrapper>
  );
}
